import type { CSSProperties } from "react";

import { getImage } from "@/generated/images";

/**
 * The replacement for next/image.
 *
 * Renders a <picture> with AVIF and WebP sources ahead of the original format,
 * so a modern browser takes the small file and an old one still gets a picture.
 * Every tag carries intrinsic width and height: without them the layout shifts
 * as images load, and the route audit fails the page for it.
 *
 * The blur placeholder is the element's own background, not a second element or
 * a JavaScript swap. The real image paints over it when it arrives, which means
 * it works with scripting disabled and needs no hydration to disappear.
 *
 * `fill` matches what next/image called fill: the image is absolutely
 * positioned and covers its nearest positioned ancestor, so the parent needs
 * `relative` on it. `sizes` matters most here, because a full-bleed element's
 * required resolution is not the same as its CSS width.
 */

export type PictureVariant = { width: number; height?: number; format: string; url: string };

/** Only the preview the server made. A background URL is a place a style value could escape. */
const SAFE_BLUR = /^data:image\/webp;base64,[A-Za-z0-9+/=]+$/;

export function Picture({
  asset,
  src,
  variants,
  blurDataUrl,
  width,
  height,
  alt,
  fill = false,
  sizes,
  priority = false,
  className = "",
  style,
  objectPosition,
}: {
  /** Path under src/assets/images without the extension: the same id the content model stores. */
  asset: string;
  /**
   * A file uploaded through the media library, served from the upload store.
   *
   * Uploads get their responsive versions when they arrive rather than at build
   * time, and `variants` lists them. An older upload made before that existed
   * has none and is served as one file.
   */
  src?: string;
  variants?: PictureVariant[];
  blurDataUrl?: string;
  width?: number;
  height?: number;
  alt: string;
  fill?: boolean;
  sizes?: string;
  /** Above the fold. Loads eagerly at high priority instead of lazily. */
  priority?: boolean;
  className?: string;
  style?: CSSProperties;
  objectPosition?: string;
}) {
  const loading = priority ? "eager" : "lazy";
  const fetchPriority = priority ? "high" : undefined;
  const decoding = priority ? "sync" : "async";
  const classes = fill ? `absolute inset-0 h-full w-full ${className}` : className;
  const position = objectPosition ? { objectPosition } : {};

  if (src) {
    const ladder = (format: string) =>
      (variants ?? [])
        .filter((variant) => variant.format === format)
        .sort((a, b) => a.width - b.width)
        .map((variant) => `${variant.url} ${variant.width}w`)
        .join(", ");

    const fallbackFormat = variants?.some((variant) => variant.format === "png") ? "png" : "jpeg";
    const avif = ladder("avif");
    const webp = ladder("webp");
    const fallback = ladder(fallbackFormat);
    const ladderSizes = sizes ?? "100vw";
    const placeholder: CSSProperties =
      blurDataUrl && SAFE_BLUR.test(blurDataUrl)
        ? { backgroundImage: `url("${blurDataUrl}")`, backgroundSize: "cover", backgroundPosition: "center" }
        : {};

    const img = (
      <img
        src={src}
        srcSet={fallback || undefined}
        sizes={fallback ? ladderSizes : undefined}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding={decoding}
        className={classes}
        style={{ ...placeholder, ...position, ...style }}
      />
    );

    if (!avif && !webp) return img;
    return (
      <picture>
        {avif ? <source type="image/avif" srcSet={avif} sizes={ladderSizes} /> : null}
        {webp ? <source type="image/webp" srcSet={webp} sizes={ladderSizes} /> : null}
        {img}
      </picture>
    );
  }

  const image = getImage(asset);

  const placeholder: CSSProperties = image.hasAlpha
    ? {}
    : { backgroundImage: `url("${image.blur}")`, backgroundSize: "cover", backgroundPosition: "center" };

  return (
    <picture>
      <source type="image/avif" srcSet={image.avif} sizes={sizes} />
      <source type="image/webp" srcSet={image.webp} sizes={sizes} />
      <img
        src={image.src}
        srcSet={image.fallback}
        sizes={sizes}
        alt={alt}
        width={image.width}
        height={image.height}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding={decoding}
        className={classes}
        style={{ ...placeholder, ...position, ...style }}
      />
    </picture>
  );
}
